import React, { useEffect, useMemo, useState } from 'react';
import './admin-dashboard.css';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { HashLoader } from 'react-spinners';
import { showToast } from '../../components/toasts/ToastNotification';
import { getDashboardData } from '../../api/analytic.api';
import { formatVietnamesePrice, getOrderStatus } from '../../utils';

const DEFAULT_STATS = {
  totalOrders: 0,
  processingOrders: 0,
  totalUsers: 0,
  weeklyRevenue: [],
  ordersByStatus: { successOrders: 0, processingOrders: 0, failedOrders: 0 },
  productsByType: [],
  recentOrders: [],
  weeklyUserGrowth: [],
  topDepositUsers: [],
  userGrowthSummary: { dailyUsers: 0, weeklyUsers: 0, monthlyUsers: 0 },
  revenueSummary: { dailyRevenue: 0, weeklyRevenue: 0, monthlyRevenue: 0, yearlyRevenue: 0 },
  totalBalance: 0,
};

const PIE_COLORS = ['#2f8fff', '#19c58f', '#ffcc00', '#ff6a55', '#7f8fff'];

const safeNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

export const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(DEFAULT_STATS);

  const greetings = useMemo(
    () => [
      'Chào mừng bạn trở lại!',
      'Chúc bạn một ngày làm việc hiệu quả!',
      'Hôm nay bạn muốn quản lý gì?',
      'Rất vui được gặp lại bạn!',
      'Sẵn sàng cho những nhiệm vụ mới chứ?',
    ],
    []
  );

  const randomGreeting = useMemo(() => greetings[Math.floor(Math.random() * greetings.length)], [greetings]);

  useEffect(() => {
    document.title = 'Admin - Trang chủ';
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await getDashboardData();

      if (res?.success) {
        const dashboardStats = res?.data?.statistics || DEFAULT_STATS;
        setStats({
          ...DEFAULT_STATS,
          ...dashboardStats,
          ordersByStatus: { ...DEFAULT_STATS.ordersByStatus, ...(dashboardStats.ordersByStatus || {}) },
          userGrowthSummary: { ...DEFAULT_STATS.userGrowthSummary, ...(dashboardStats.userGrowthSummary || {}) },
          revenueSummary: { ...DEFAULT_STATS.revenueSummary, ...(dashboardStats.revenueSummary || {}) },
          weeklyRevenue: Array.isArray(dashboardStats.weeklyRevenue) ? dashboardStats.weeklyRevenue : [],
          productsByType: Array.isArray(dashboardStats.productsByType) ? dashboardStats.productsByType : [],
          recentOrders: Array.isArray(dashboardStats.recentOrders) ? dashboardStats.recentOrders : [],
          weeklyUserGrowth: Array.isArray(dashboardStats.weeklyUserGrowth) ? dashboardStats.weeklyUserGrowth : [],
          topDepositUsers: Array.isArray(dashboardStats.topDepositUsers) ? dashboardStats.topDepositUsers : [],
        });
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const productsCount = useMemo(
    () => stats.productsByType.reduce((sum, product) => sum + safeNumber(product?.value), 0),
    [stats.productsByType]
  );

  const dailyAOVData = useMemo(
    () =>
      stats.weeklyRevenue.map((item) => {
        const orders = safeNumber(item?.orders);
        const revenue = safeNumber(item?.revenue);
        return {
          date: item?.date || '-',
          aov: orders > 0 ? Number((revenue / orders).toFixed(2)) : 0,
        };
      }),
    [stats.weeklyRevenue]
  );

  const orderStatusData = useMemo(
    () => [
      { name: 'Hoàn thành', value: safeNumber(stats.ordersByStatus?.successOrders) },
      { name: 'Đang xử lý', value: safeNumber(stats.ordersByStatus?.processingOrders) },
      { name: 'Đã hủy', value: safeNumber(stats.ordersByStatus?.failedOrders) },
    ],
    [stats.ordersByStatus]
  );

  const primaryMetrics = useMemo(
    () => [
      { label: 'Tổng đơn hàng', value: safeNumber(stats.totalOrders) },
      { label: 'Tổng người dùng', value: safeNumber(stats.totalUsers) },
      { label: 'Doanh thu hôm nay', value: formatVietnamesePrice(safeNumber(stats.revenueSummary?.dailyRevenue)) },
      { label: 'Tổng số dư coin', value: formatVietnamesePrice(safeNumber(stats.totalBalance)) },
    ],
    [stats.totalOrders, stats.totalUsers, stats.revenueSummary?.dailyRevenue, stats.totalBalance]
  );

  const subMetrics = useMemo(
    () => [
      { label: 'Đơn thành công', value: safeNumber(stats.ordersByStatus?.successOrders) },
      { label: 'Đơn đang chờ', value: safeNumber(stats.processingOrders) },
      { label: 'Đơn đã hủy', value: safeNumber(stats.ordersByStatus?.failedOrders) },
      { label: 'Doanh thu tháng', value: formatVietnamesePrice(safeNumber(stats.revenueSummary?.monthlyRevenue)) },
      { label: 'Sản phẩm', value: productsCount },
      { label: 'User mới tháng', value: safeNumber(stats.userGrowthSummary?.monthlyUsers) },
    ],
    [
      stats.ordersByStatus?.successOrders,
      stats.processingOrders,
      stats.ordersByStatus?.failedOrders,
      stats.revenueSummary?.monthlyRevenue,
      productsCount,
      stats.userGrowthSummary?.monthlyUsers,
    ]
  );

  const formatTooltipCurrency = (value) => formatVietnamesePrice(safeNumber(value));

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard-shell">
      <section className="admin-dashboard-hero">
        <div>
          <p className="admin-dashboard-kicker">Admin Dashboard</p>
          <h1>{randomGreeting}</h1>
          <p className="admin-dashboard-subtitle">Theo dõi vận hành theo thời gian thực và xử lý nhanh các điểm nghẽn.</p>
        </div>
        <div className="admin-dashboard-date-box">
          <p>Hôm nay</p>
          <h3>{new Date().toLocaleDateString('vi-VN')}</h3>
          <span>{new Date().toLocaleTimeString('vi-VN')}</span>
        </div>
      </section>

      <section className="admin-dashboard-grid metrics-grid">
        {primaryMetrics.map((metric) => (
          <article key={metric.label} className="admin-stat-card admin-stat-card-primary">
            <p>{metric.label}</p>
            <h3>{metric.value}</h3>
          </article>
        ))}
      </section>

      <section className="admin-dashboard-grid sub-metrics-grid">
        {subMetrics.map((metric) => (
          <article key={metric.label} className="admin-stat-card">
            <p>{metric.label}</p>
            <h4>{metric.value}</h4>
          </article>
        ))}
      </section>

      <section className="admin-dashboard-grid chart-grid">
        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Doanh thu tuần</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.weeklyRevenue} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3e86d8" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#3e86d8" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <YAxis
                  tick={{ fill: 'var(--sub-text)', fontSize: 12 }}
                  tickFormatter={(rev) => formatVietnamesePrice(safeNumber(rev))}
                />
                <Tooltip formatter={formatTooltipCurrency} />
                <Area type="monotone" dataKey="revenue" stroke="#3e86d8" fill="url(#colorRevenue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Đơn hàng theo trạng thái</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={88}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => safeNumber(value).toLocaleString('vi-VN')} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Đơn hàng hằng ngày</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.weeklyRevenue}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <YAxis tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <Tooltip formatter={(value) => safeNumber(value).toLocaleString('vi-VN')} />
                <Legend />
                <Bar dataKey="orders" fill="#19c58f" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Danh mục sản phẩm</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.productsByType}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={88}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {stats.productsByType.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => safeNumber(value).toLocaleString('vi-VN')} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Tăng trưởng người dùng</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.weeklyUserGrowth}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <YAxis tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <Tooltip formatter={(value) => safeNumber(value).toLocaleString('vi-VN')} />
                <Legend />
                <Bar dataKey="users" fill="#ffcc00" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Giá trị đơn hàng trung bình</h2>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyAOVData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: 'var(--sub-text)', fontSize: 12 }} />
                <YAxis tick={{ fill: 'var(--sub-text)', fontSize: 12 }} tickFormatter={formatTooltipCurrency} />
                <Tooltip formatter={formatTooltipCurrency} />
                <Legend />
                <Line type="monotone" dataKey="aov" stroke="#ffcc00" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      <section className="admin-dashboard-grid table-grid">
        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Người dùng nạp nhiều nhất</h2>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-dashboard-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tên khách hàng</th>
                  <th>Số dư</th>
                  <th>Tổng đã nạp</th>
                </tr>
              </thead>
              <tbody>
                {stats.topDepositUsers.length > 0 ? (
                  stats.topDepositUsers.map((user) => (
                    <tr key={user._id}>
                      <td>{user._id}</td>
                      <td>{user.username || 'N/A'}</td>
                      <td>{safeNumber(user.balance).toLocaleString('vi-VN')}đ</td>
                      <td>{safeNumber(user.total_amount_deposited).toLocaleString('vi-VN')}đ</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Không có người dùng</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Đơn hàng gần đây</h2>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-dashboard-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tên khách hàng</th>
                  <th>Ngày</th>
                  <th>Số tiền</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.length > 0 ? (
                  stats.recentOrders.map((order) => (
                    <tr key={order.orderId}>
                      <td>{order.orderId}</td>
                      <td>{order.username || 'N/A'}</td>
                      <td>{new Date(order.createdAt).toLocaleString('vi-VN')}</td>
                      <td>{safeNumber(order.amount).toLocaleString('vi-VN')}đ</td>
                      <td>
                        <span className={`admin-dashboard-status admin-dashboard-${getOrderStatus(order.status).class}`}>
                          {getOrderStatus(order.status).text}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Không có đơn hàng</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </div>
  );
};
